import { CLink } from '@/CLink';
import { Dialog, DialogClose, DialogContent, DialogOpen } from '@/DialogPrimitive';
import { PlainBtn } from '@/buttons';
import { ReactComponent as CloseIcon } from '@a/icons/close.svg';
import { ReactComponent as MenuIcon } from '@a/icons/menu.svg';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import useRouteChange from '@s/hooks/useRouteChange';
import useWindowSize from '@s/hooks/useWindowSize';
import { Fragment, useState } from 'react';
import { MobSubNav } from './MobSubNav';
import { HomeLogo } from './Nav';
import { navLinkStyles } from './NavLinkStyles';
import { navLinks } from './NavLinks';
import { theme } from './styles/GlobalStyles';
import { flexCenter, flexSpace, px } from './styles/classes';

const overlayStyles = css`
    display: none;
`;

const contentStyles = css`
    background-color: ${theme.colors.white};
    padding-bottom: 32px;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
`;

const NavMenu = styled.nav<{ subDialogOpen: boolean }>`
    ${flexCenter};
    flex-direction: column;
    transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;
    height: calc(100% - 35px); // logo height
    overflow-y: auto;
    gap: 48px;

    @media (min-height: 600px) {
        gap: 72px;
    }

    ${({ subDialogOpen }) =>
        subDialogOpen &&
        css`
            visibility: ${subDialogOpen ? 'hidden' : 'visible'};
            pointer-events: ${subDialogOpen ? 'none' : 'auto'};
            opacity: ${subDialogOpen ? 0 : 1};
        `};
`;

const Top = styled.div`
    ${px};
    ${flexSpace};
    margin-bottom: 40px;
    padding-top: 16px;
    padding-bottom: 16px;
    background-color: ${({ theme }) => theme.colors.gray7};

    > button > svg > path {
        stroke: ${({ theme }) => theme.colors.blue1};
    }
`;

export const MobNav = () => {
    const [open, setOpen] = useState(false);
    const [subDialogOpen, setSubDialogOpen] = useState(false);

    const { width } = useWindowSize();

    useRouteChange(setOpen);

    return width < 1280 ? (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogOpen>
                <PlainBtn
                    aria-label="open navigation menu"
                    css={theme => css`
                        path {
                            stroke: ${theme.colors.blue3};
                        }
                        @media (min-width: 1280px) {
                            display: none;
                        }
                    `}
                >
                    <MenuIcon />
                </PlainBtn>
            </DialogOpen>

            <DialogContent
                contentStyles={contentStyles}
                overlayStyles={overlayStyles}
                title="website main navigation menu"
            >
                <Top>
                    <HomeLogo />
                    <DialogClose>
                        <PlainBtn data-mobile-expanded="true" aria-label="close the nav menu">
                            <CloseIcon />
                        </PlainBtn>
                    </DialogClose>
                </Top>
                <NavMenu subDialogOpen={subDialogOpen}>
                    {navLinks.map((data, i) =>
                        data.links ? (
                            <MobSubNav
                                key={i}
                                text={data.text}
                                links={data.links}
                                setSubDialogOpen={setSubDialogOpen}
                            />
                        ) : (
                            <CLink
                                to={data.link}
                                css={navLinkStyles}
                                key={i}
                                className="chevron-space"
                            >
                                {data.text}
                            </CLink>
                        )
                    )}
                </NavMenu>
            </DialogContent>
        </Dialog>
    ) : (
        <Fragment />
    );
};
